// B2C Frontend - Deployment Trigger: 2025-10-31 09:30 UTC
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

// Layout Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';

// Pages
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import BundlesPage from './pages/BundlesPage';
import BundleDetailPage from './pages/BundleDetailPage';
import WishlistPage from './pages/WishlistPage';
import CategoryPage from './pages/CategoryPage';
import CategoriesPage from './pages/CategoriesPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import AccountPage from './pages/account/AccountPage';
import OrdersPage from './pages/account/OrdersPage';
import OrderHistoryPage from './pages/account/OrderHistoryPage';
import OrderTemplatesPage from './pages/account/OrderTemplatesPage';
import OrderDetailPage from './pages/account/OrderDetailPage';
import ProfileSettings from './pages/ProfileSettings';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import NotificationsPage from './pages/NotificationsPage';
import OrderTrackingPage from './pages/OrderTrackingPage';

// Admin Pages
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import EnhancedDashboard from './pages/admin/EnhancedDashboard';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminBrands from './pages/admin/AdminBrands';
import AdminWasteReports from './pages/admin/AdminWasteReports';
import AdminReturnsPage from './pages/admin/AdminReturnsPage';
import WasteAnalytics from './pages/admin/WasteAnalytics';
import BusinessAnalytics from './pages/admin/BusinessAnalytics';
import AdminBusinessIntelligence from './pages/admin/AdminBusinessIntelligence';
import AdminProfitAnalysis from './pages/admin/AdminProfitAnalysis';
import ProfitabilityAnalysis from './pages/admin/ProfitabilityAnalysis';
import InventoryOptimization from './pages/admin/InventoryOptimization';
import AnalyticsManagement from './pages/admin/AnalyticsManagement';
import AnalyticsTest from './pages/admin/AnalyticsTest';
import RealTimeAnalytics from './pages/admin/RealTimeAnalytics';
import ConversionFunnels from './pages/admin/ConversionFunnels';
import AdminCashFlowPrediction from './pages/admin/AdminCashFlowPrediction';
import AdminProducts from './pages/admin/AdminProducts';
import AdminProductDetail from './pages/admin/AdminProductDetail';
import AdminProductEdit from './pages/admin/AdminProductEdit';
import AdminCategories from './pages/admin/AdminCategories';
import AdminBundles from './pages/admin/AdminBundles';
import InventoryManagement from './pages/admin/InventoryManagement';
import AdminOrders from './pages/admin/AdminOrders';
import AdminAgentOrders from './pages/admin/AdminAgentOrders';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminCompanies from './pages/admin/AdminCompanies';
import AdminContracts from './pages/admin/AdminContracts';
import AdminSettings from './pages/admin/AdminSettings';
import AdminReviews from './pages/admin/AdminReviews';
import CreditDebug from './pages/admin/CreditDebug';
import PaymentDetailsPage from './pages/admin/PaymentDetailsPage';
import AdminCompaniesDebt from './pages/admin/AdminCompaniesDebt';
import AdminCompanyDebt from './pages/admin/AdminCompanyDebt';
import AdminPaymentReports from './pages/admin/AdminPaymentReports';
import AdminFeaturesShowcase from './pages/admin/AdminFeaturesShowcase';
import FeatureToggles from './pages/admin/FeatureToggles';
import FieldAgentsPage from './pages/admin/fieldAgents/FieldAgentsPage';
import SalesTargetsPage from './pages/admin/fieldAgents/SalesTargetsPage';
import AgentPerformancePage from './pages/admin/fieldAgents/AgentPerformancePage';
import AdminSuppliers from './pages/admin/AdminSuppliers';
import AdminPurchaseOrders from './pages/admin/AdminPurchaseOrders';
import PurchaseOrderDetail from './pages/admin/PurchaseOrderDetail';
import CreateGoodsReceipt from './pages/admin/CreateGoodsReceipt';
import AdminGoodsReceipts from './pages/admin/AdminGoodsReceipts';
import AdminChecksPage from './pages/admin/AdminChecksPage';

// NOTE: B2B, Agent, and Seller pages are NOT imported in B2C
// They are only available in the B2B frontend (client-b2b)
// B2C is for regular consumers only!

// Context Providers
import { AuthProvider } from './contexts/AuthContext';
import { FeatureToggleProvider } from './contexts/FeatureToggleContext';
import { CartProvider } from './contexts/CartContext';
import { ThemeProvider } from './hooks/useTheme';
import { SocketProvider } from './contexts/SocketContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { EnhancedAnalyticsProvider } from './contexts/EnhancedAnalyticsContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';

// Analytics Components
import CookieConsent from './components/analytics/CookieConsent';
import PageTracker from './components/analytics/PageTracker';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Accessibility Components
import AccessibilityWidget from './components/accessibility/AccessibilityWidget';

// UI Components
import ScrollToTop from './components/ui/ScrollToTop';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  // Fix Android rotation issue (especially for Galaxy Tab A7)
  React.useEffect(() => {
    const handleOrientationChange = () => {
      // Method 1: Force viewport recalculation
      const viewport = document.querySelector('meta[name=viewport]');
      if (viewport) {
        const currentContent = viewport.getAttribute('content');
        // Temporarily change viewport
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0001');

        setTimeout(() => {
          // Restore original viewport
          viewport.setAttribute('content', currentContent);

          // Method 2: Force window resize event
          window.dispatchEvent(new Event('resize'));

          // Method 3: Trigger reflow (for Android tablets)
          document.documentElement.style.height = 'initial';
          setTimeout(() => {
            document.documentElement.style.height = '';
          }, 20);
        }, 100);
      }

      // Method 4: Force full repaint (aggressive for stubborn Android devices)
      const root = document.getElementById('root');
      if (root) {
        root.style.opacity = '0.99';
        setTimeout(() => {
          root.style.opacity = '';
        }, 50);
      }
    };

    // Listen to multiple events for better Android compatibility
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);

    // For Android Chrome/Samsung Internet
    if (window.screen && window.screen.orientation) {
      window.screen.orientation.addEventListener('change', handleOrientationChange);
    }

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
      if (window.screen && window.screen.orientation) {
        window.screen.orientation.removeEventListener('change', handleOrientationChange);
      }
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AccessibilityProvider>
          <AuthProvider>
            <FeatureToggleProvider>
              <AnalyticsProvider>
                <EnhancedAnalyticsProvider>
                  <SocketProvider>
                    <CartProvider>
                      <Router>
                        <div className="min-h-screen flex flex-col bg-gray-50">
                          <Routes>
                            {/* B2B Trade Routes - REMOVED FROM B2C */}
                            {/* /trade route is only available in B2B frontend (lumelb2b.com) */}

                            {/* Admin Routes */}
                            <Route path="/admin/*" element={
                              <ProtectedRoute roles={['admin']}>
                                <AdminLayout>
                                  <Routes>
                                    {/* דשבורד ראשי - משופר */}
                                    <Route path="/" element={<EnhancedDashboard />} />

                                    {/* ניתוח עסקי מאוחד */}
                                    <Route path="/business-analytics" element={<BusinessAnalytics />} />
                                    <Route path="/profitability" element={<ProfitabilityAnalysis />} />
                                    <Route path="/inventory-optimization" element={<InventoryOptimization />} />
                                    <Route path="/analytics-management" element={<AnalyticsManagement />} />
                                    <Route path="/analytics-test" element={<AnalyticsTest />} />
                                    <Route path="/real-time-analytics" element={<RealTimeAnalytics />} />
                                    <Route path="/conversion-funnels" element={<ConversionFunnels />} />

                                    {/* דשבורדים ישנים - לתמיכה לאחור */}
                                    <Route path="/classic" element={<AdminDashboard />} />
                                    <Route path="/analytics" element={<AdminAnalytics />} />
                                    <Route path="/business-intelligence" element={<AdminBusinessIntelligence />} />
                                    <Route path="/profit-analysis" element={<AdminProfitAnalysis />} />
                                    <Route path="/cash-flow-prediction" element={<AdminCashFlowPrediction />} />
                                    <Route path="/products" element={<AdminProducts />} />
                                    <Route path="/products/edit/:productId" element={<AdminProductEdit />} />
                                    <Route path="/products/:productId" element={<AdminProductDetail />} />
                                    <Route path="/categories" element={<AdminCategories />} />
                                    <Route path="/brands" element={<AdminBrands />} />
                                    <Route path="/bundles" element={<AdminBundles />} />
                                    <Route path="/inventory" element={<InventoryManagement />} />
                                    <Route path="/waste-reports" element={<AdminWasteReports />} />
                                    <Route path="/waste-analytics" element={<WasteAnalytics />} />
                                    <Route path="/returns" element={<AdminReturnsPage />} />
                                    <Route path="/orders" element={<AdminOrders />} />
                                    <Route path="/agent-orders" element={<AdminAgentOrders />} />
                                    <Route path="/payments/:paymentId" element={<PaymentDetailsPage />} />
                                    <Route path="/companies-debt" element={<AdminCompaniesDebt />} />
                                    <Route path="/companies-debt/:companyId" element={<AdminCompanyDebt />} />
                                    <Route path="/payment-reports" element={<AdminPaymentReports />} />
                                    <Route path="/customers" element={<AdminCustomers />} />
                                    <Route path="/companies" element={<AdminCompanies />} />
                                    <Route path="/contracts" element={<AdminContracts />} />
                                    <Route path="/reviews" element={<AdminReviews />} />
                                    <Route path="/settings" element={<AdminSettings />} />
                                    <Route path="/credit-debug" element={<CreditDebug />} />
                                    <Route path="/features" element={<AdminFeaturesShowcase />} />
                                    <Route path="/feature-toggles" element={<FeatureToggles />} />
                                    <Route path="/field-agents" element={<FieldAgentsPage />} />
                                    <Route path="/sales-targets" element={<SalesTargetsPage />} />
                                    <Route path="/agent-performance" element={<AgentPerformancePage />} />
                                    <Route path="/agent-performance/:id" element={<AgentPerformancePage />} />

                                    {/* Procurement Routes */}
                                    <Route path="/suppliers" element={<AdminSuppliers />} />
                                    <Route path="/purchase-orders" element={<AdminPurchaseOrders />} />
                                    <Route path="/purchase-orders/:id" element={<PurchaseOrderDetail />} />
                                    <Route path="/goods-receipts" element={<AdminGoodsReceipts />} />
                                    <Route path="/goods-receipts/new" element={<CreateGoodsReceipt />} />
                                    <Route path="/checks" element={<AdminChecksPage />} />
                                  </Routes>
                                </AdminLayout>
                              </ProtectedRoute>
                            } />

                            {/* Field Agent Routes - REMOVED FROM B2C */}
                            {/* Agent routes are only available in B2B frontend (lumelb2b.com) */}

                            {/* Public Routes */}
                            <Route path="/*" element={
                              <>
                                <Navbar />
                                <main id="main-content" className="flex-grow" role="main" aria-label="תוכן ראשי">
                                  <Routes>
                                    <Route path="/" element={<HomePage />} />
                                    <Route path="/products" element={<ProductsPage />} />
                                    <Route path="/products/:productId" element={<ProductDetailPage />} />
                                    <Route path="/bundles" element={<BundlesPage />} />
                                    <Route path="/bundles/:id" element={<BundleDetailPage />} />
                                    <Route path="/wishlist" element={<WishlistPage />} />
                                    <Route path="/categories" element={<CategoriesPage />} />
                                    <Route path="/categories/:categorySlug" element={<CategoryPage />} />
                                    <Route path="/cart" element={<CartPage />} />
                                    <Route path="/checkout" element={
                                      <ProtectedRoute>
                                        <CheckoutPage />
                                      </ProtectedRoute>
                                    } />
                                    {/* B2B Fast Checkout - REMOVED FROM B2C */}
                                    <Route path="/login" element={<LoginPage />} />
                                    <Route path="/register" element={<RegisterPage />} />
                                    <Route path="/account" element={
                                      <ProtectedRoute>
                                        <AccountPage />
                                      </ProtectedRoute>
                                    } />
                                    {/* Order detail must come BEFORE the generic /orders route */}
                                    <Route path="/orders/:orderId" element={
                                      <ProtectedRoute>
                                        <OrderDetailPage />
                                      </ProtectedRoute>
                                    } />
                                    <Route path="/orders" element={
                                      <ProtectedRoute>
                                        <OrdersPage />
                                      </ProtectedRoute>
                                    } />
                                    <Route path="/account/order-history" element={
                                      <ProtectedRoute>
                                        <OrderHistoryPage />
                                      </ProtectedRoute>
                                    } />
                                    <Route path="/account/order-templates" element={
                                      <ProtectedRoute>
                                        <OrderTemplatesPage />
                                      </ProtectedRoute>
                                    } />
                                    <Route path="/track" element={<OrderTrackingPage />} />
                                    <Route path="/track/:orderNumber" element={<OrderTrackingPage />} />
                                    <Route path="/profile" element={
                                      <ProtectedRoute>
                                        <ProfileSettings />
                                      </ProtectedRoute>
                                    } />
                                    <Route path="/notifications" element={
                                      <ProtectedRoute>
                                        <NotificationsPage />
                                      </ProtectedRoute>
                                    } />
                                    {/* Price Negotiations - REMOVED FROM B2C */}
                                    {/* Negotiations are only available in B2B frontend (lumelb2b.com) */}
                                    {/* B2C customers pay fixed prices */}
                                    <Route path="/about" element={<AboutPage />} />
                                    <Route path="/contact" element={<ContactPage />} />

                                    {/* 404 Page */}
                                    <Route path="*" element={
                                      <div className="min-h-screen flex items-center justify-center">
                                        <div className="text-center">
                                          <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                                          <p className="text-gray-600 mb-4">הדף שחיפשת לא נמצא</p>
                                          <a href="/" className="btn btn-primary">חזור לעמוד הבית</a>
                                        </div>
                                      </div>
                                    } />
                                  </Routes>
                                </main>
                                <Footer />
                              </>
                            } />
                          </Routes>

                          {/* Accessibility Widget */}
                          <AccessibilityWidget />

                          {/* Scroll to Top Button */}
                          <ScrollToTop />

                          {/* Analytics Components */}
                          {/* Analytics page tracking */}
                          <PageTracker />
                          <CookieConsent />

                          {/* Toast Notifications */}
                          <Toaster
                            position="top-center"
                            toastOptions={{
                              duration: 4000,
                              style: {
                                background: '#363636',
                                color: '#fff',
                              },
                              success: {
                                style: {
                                  background: '#10b981',
                                },
                              },
                              error: {
                                style: {
                                  background: '#ef4444',
                                },
                              },
                            }}
                          />
                        </div>
                      </Router>
                    </CartProvider>
                  </SocketProvider>
                </EnhancedAnalyticsProvider>
              </AnalyticsProvider>
            </FeatureToggleProvider>
          </AuthProvider>
        </AccessibilityProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
